import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { localBusinessSchema } from '../lib/structuredData';
import { 
  Phone, 
  MessageCircle, 
  CheckCircle2, 
  Shield, 
  Clock, 
  Award,
  Zap,
  ArrowRight,
  Users,
  TrendingUp,
  MapPin,
  HeadphonesIcon,
  Settings,
  Wrench,
  ClipboardCheck,
  Building2,
  Droplets,
  BadgeCheck,
  Siren
} from 'lucide-react';

const Home = () => {
  const services = [
    {
      icon: Settings,
      title: 'Air Conditioning Installation',
      description: 'Professional supply and installation of high-efficiency air conditioning systems for domestic and commercial properties.',
    },
    {
      icon: Wrench,
      title: 'Repairs & Breakdowns',
      description: 'Fast diagnosis and repair of all air conditioning and HVAC faults. 24/7 call-out service available.',
    },
    {
      icon: ClipboardCheck,
      title: 'Servicing & Maintenance',
      description: 'Keep your systems running at peak performance with expert servicing and maintenance plans.',
    },
    {
      icon: Building2,
      title: 'Commercial HVAC',
      description: 'Bespoke commercial HVAC solutions for offices, retail, restaurants and large commercial spaces.',
    },
    {
      icon: HeadphonesIcon,
      title: '24/7 Call Out',
      description: 'Emergency call-out service available 24 hours a day, 7 days a week for urgent repairs.',
    },
    {
      icon: Zap,
      title: 'Mechanical & Electrical',
      description: 'Complete mechanical and electrical HVAC services for commercial peace of mind.',
    },
  ];

  const whyChoose = [
    { icon: Shield, title: 'Expert Engineers', desc: 'Highly trained & certified HVAC specialists' },
    { icon: Award, title: 'Quality Workmanship', desc: 'Premium installations backed by warranty' },
    { icon: Zap, title: 'Fast Response Times', desc: 'Same-day & emergency call-outs available' },
    { icon: Users, title: 'Customer Focused', desc: 'Reliable support before & after install' },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Air Conditioning Installation London"
        description="Premium HVAC and air conditioning installation across London. F-Gas certified, REFCOM compliant. Get an instant AI-powered quote in 60 seconds. 24/7 emergency call-outs."
        path="/"
        jsonLd={localBusinessSchema}
      />
      {/* Hero Section - Background image with overlay (matches About / Services / Commercial / Contact) */}
      <section className="relative min-h-[78vh] sm:min-h-[88vh] flex items-center overflow-hidden" data-testid="home-hero">
        {/* Full-bleed background image with dark overlay */}
        <div className="absolute inset-0">
          <img
            src="/images/deep-control-van-canary-wharf.jpg"
            alt="Deep Control HVAC Van in Canary Wharf London"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,14,20,0.55)] via-[rgba(10,14,20,0.45)] to-[rgba(10,14,20,0.70)]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full z-10 py-10 sm:py-16 lg:py-20">
          <div className="max-w-3xl space-y-5 sm:space-y-7">
            <span className="inline-block text-[10px] bg-white/5 px-4 py-1.5 sm:py-2 rounded-full text-[#FF8C00] font-bold border border-[#FF8C00]/20 tracking-[2px] backdrop-blur-sm">
              SERVING LONDON & SURROUNDING AREAS
            </span>

            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.08] sm:leading-[1.05] tracking-tight">
              Air Conditioning & <span className="text-[#FF8C00]">HVAC Solutions</span> Across London
            </h1>

            <p className="text-gray-300 text-lg sm:text-xl leading-relaxed max-w-2xl">
              Deep Control specialises in the installation, repair, and maintenance of air conditioning and HVAC systems for residential and commercial properties across London.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 pt-1 sm:pt-2">
              <div className="flex items-center gap-2 bg-white/5 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg backdrop-blur-sm">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#FF8C00]/20 flex items-center justify-center flex-shrink-0">
                  <Shield size={14} className="text-[#FF8C00] sm:hidden" />
                  <Shield size={16} className="text-[#FF8C00] hidden sm:block" />
                </div>
                <div className="text-left">
                  <span className="text-xs font-bold block leading-tight">F-Gas</span>
                  <span className="text-[10px] text-gray-400 leading-tight">Compliant</span>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg backdrop-blur-sm">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#FF8C00]/20 flex items-center justify-center flex-shrink-0">
                  <Award size={14} className="text-[#FF8C00] sm:hidden" />
                  <Award size={16} className="text-[#FF8C00] hidden sm:block" />
                </div>
                <div className="text-left">
                  <span className="text-xs font-bold block leading-tight">Certified</span>
                  <span className="text-[10px] text-gray-400 leading-tight">Engineers</span>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg backdrop-blur-sm">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#FF8C00]/20 flex items-center justify-center flex-shrink-0">
                  <BadgeCheck size={14} className="text-[#FF8C00] sm:hidden" />
                  <BadgeCheck size={16} className="text-[#FF8C00] hidden sm:block" />
                </div>
                <div className="text-left">
                  <span className="text-xs font-bold block leading-tight">REFCOM</span>
                  <span className="text-[10px] text-gray-400 leading-tight">Certified</span>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg backdrop-blur-sm">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#FF8C00]/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={14} className="text-[#FF8C00] sm:hidden" />
                  <CheckCircle2 size={16} className="text-[#FF8C00] hidden sm:block" />
                </div>
                <div className="text-left">
                  <span className="text-xs font-bold block leading-tight">Fully Insured</span>
                  <span className="text-[10px] text-gray-400 leading-tight">& Guaranteed</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4">
              <Link to="/ai-estimator">
                <Button className="bg-[#FF8C00] hover:bg-[#e67e00] text-white font-bold px-6 sm:px-8 py-5 sm:py-6 rounded-lg text-base sm:text-base transition-all shadow-lg shadow-[#FF8C00]/30 w-full sm:w-auto" data-testid="home-hero-cta-quote">
                  Get Instant Quote
                </Button>
              </Link>
              <a href="tel:+442080599941">
                <Button className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 sm:px-8 py-5 sm:py-6 rounded-lg text-base sm:text-base transition-all shadow-lg shadow-red-600/40 w-full sm:w-auto" data-testid="home-hero-cta-emergency">
                  <Siren size={16} className="mr-2 animate-pulse sm:hidden" />
                  <Siren size={18} className="mr-2 animate-pulse hidden sm:inline" /> Emergency Call Out
                </Button>
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-5 sm:gap-6 pt-1 sm:pt-2 text-sm sm:text-base">
              <a href="tel:02080599941" className="flex items-center gap-2 text-white hover:text-[#FF8C00] transition-colors">
                <Phone size={18} className="text-[#FF8C00]" />
                <span className="font-bold">020 8059 9941</span>
              </a>
              <a href="https://wa.me/447990547352" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#FF8C00] hover:text-[#e67e00] transition-colors">
                <MessageCircle size={18} />
                <span className="font-semibold">WhatsApp Us</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-[#0d1829]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF8C00] font-bold text-[11px] tracking-[3px] uppercase mb-3">OUR SERVICES</p>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">Complete Climate Control Solutions</h2>
            <p className="text-gray-400 mt-4 text-lg">Expert installations, repairs and servicing for homes and businesses across London.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <div key={idx} className="bg-[#121820]/80 backdrop-blur-sm border border-white/5 rounded-2xl p-8 hover:border-[#FF8C00]/50 transition-all duration-300 group">
                  <div className="w-16 h-16 mb-5 rounded-full bg-[#FF8C00]/10 flex items-center justify-center group-hover:bg-[#FF8C00]/20 transition-colors">
                    <Icon size={32} className="text-[#FF8C00]" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 group-hover:text-[#FF8C00] transition-colors">{service.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Instant AI Quote Section */}
      <section className="py-16 bg-[#0f1b2d]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Video Container */}
            <div className="relative h-[400px] rounded-2xl overflow-hidden order-2 lg:order-1 flex items-center justify-center bg-[#0A0E14]">
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                poster="/videos/ai-estimator-poster.jpg"
                className="w-full h-full object-contain"
                data-testid="home-ai-estimator-video"
              >
                <source src="/videos/ai-estimator.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            
            <div className="order-1 lg:order-2">
              <span className="inline-block bg-[#FF8C00]/10 text-[#FF8C00] text-xs font-bold px-4 py-2 rounded-full border border-[#FF8C00]/30 mb-4">
                NEW: THE INTELLIGENT AC ESTIMATOR
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
                Get an Instant Quote with <span className="text-[#FF8C00]">AI Workspace Visualization</span>.
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                Upload a photo. Get your exact price. See your breakdown. View your unit visualized. Zero obligation, 100% free.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#FF8C00] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">AI calculates exact room size from your photo</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#FF8C00] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">Full price breakdown — every line, no surprises</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#FF8C00] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">See your AC unit photorealistically rendered on your wall</span>
                </li>
              </ul>
              <Link to="/ai-estimator">
                <Button className="bg-[#FF8C00] hover:bg-[#e67e00] text-white font-bold px-8 py-6 rounded-lg text-base" data-testid="home-try-ai-estimator-btn">
                  Try Our AI Estimator <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Deep Control - FINAL SECTION ON HOME */}
      <section className="py-16 bg-[#0d1829]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF8C00] font-bold text-[11px] tracking-[3px] uppercase mb-3">WHY CHOOSE DEEP CONTROL?</p>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">London Wide Coverage</h2>
            <p className="text-gray-400 mt-4 text-lg">& Unmatched Expertise</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChoose.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="text-center">
                  <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-[#FF8C00]/10 flex items-center justify-center">
                    <Icon size={36} className="text-[#FF8C00]" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;